import { useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

/**
 * Horizontal-swipe navigation between the three primary tabs:
 *   /  →  /marketplace  →  /providers  (and back)
 *
 * Constraints:
 *  - Only active on those three routes.
 *  - Gesture must be predominantly horizontal (|dx| > 1.4 * |dy|).
 *  - Must travel at least SWIPE_THRESHOLD px.
 *  - Ignored if the touch starts on an element marked [data-no-swipe]
 *    or inside a horizontally-scrollable ancestor (e.g. chip rows,
 *    image carousels).
 *  - Edge-swipes (start within 24px of the left edge) are ignored to
 *    avoid clashing with the system back gesture.
 */
const TAB_ORDER = ['/', '/marketplace', '/providers'] as const;
const SWIPE_THRESHOLD = 60;
const EDGE_IGNORE_PX = 24;

const isHorizontallyScrollable = (el: HTMLElement | null): boolean => {
  let node: HTMLElement | null = el;
  while (node && node !== document.body) {
    if (node.dataset && node.dataset.noSwipe === 'true') return true;
    const style = window.getComputedStyle(node);
    const overflowX = style.overflowX;
    if (
      (overflowX === 'auto' || overflowX === 'scroll') &&
      node.scrollWidth > node.clientWidth + 1
    ) {
      return true;
    }
    node = node.parentElement;
  }
  return false;
};

export const useSwipeTabs = () => {
  const history = useHistory();
  const location = useLocation();

  useEffect(() => {
    const currentIndex = TAB_ORDER.indexOf(location.pathname as typeof TAB_ORDER[number]);
    if (currentIndex === -1) return;

    let startX = 0;
    let startY = 0;
    let startTime = 0;
    let blocked = false;

    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length !== 1) {
        blocked = true;
        return;
      }
      const t = e.touches[0];
      startX = t.clientX;
      startY = t.clientY;
      startTime = Date.now();
      blocked =
        startX < EDGE_IGNORE_PX ||
        startX > window.innerWidth - EDGE_IGNORE_PX ||
        isHorizontallyScrollable(e.target as HTMLElement);
    };

    const onTouchEnd = (e: TouchEvent) => {
      if (blocked) return;
      const t = e.changedTouches[0];
      if (!t) return;
      const dx = t.clientX - startX;
      const dy = t.clientY - startY;
      const dt = Date.now() - startTime;

      if (Math.abs(dx) < SWIPE_THRESHOLD) return;
      if (Math.abs(dx) < Math.abs(dy) * 1.4) return;
      if (dt > 600) return; // too slow → probably not a swipe

      if (dx < 0 && currentIndex < TAB_ORDER.length - 1) {
        history.push(TAB_ORDER[currentIndex + 1]);
      } else if (dx > 0 && currentIndex > 0) {
        history.push(TAB_ORDER[currentIndex - 1]);
      }
    };

    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchend', onTouchEnd, { passive: true });
    return () => {
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchend', onTouchEnd);
    };
  }, [location.pathname, history]);
};
